import { useTranslation } from 'react-i18next';
import type { ColumnVisibilityState } from '@tanstack/react-table';

import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import { Spinner } from '@/components/atoms/Spinner';
import Tooltip from '@/components/atoms/Tooltip';
import PageLayout from '@/components/Layout/PageLayout';
import { ColumnMenu } from '@/components/organisms/DataTable';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import type { AlertAcknowledgement, Alert as AlertEntry } from '@/domain/alerts';
import type { ActiveBioscope } from '@/domain/bioscopes';
import type { ClientInfo } from '@/domain/client';

import AddBioscope from './AddBioscope';
import { COLUMN_METADATA } from './bioscopeListColumns';
import BioscopeTable from './components/BioscopeTable';
import Filter from './components/Filter';

import styles from './BioscopeListContainer.module.scss';

interface BioscopeListViewProps {
    hasError: boolean;
    /** Try again for the requests that failed. */
    retry: RetryProps;
    loading: boolean;
    activeCount: number;
    onlineCount: number;
    bioscopes: ActiveBioscope[];
    alerts: AlertEntry[];
    acknowledgements: AlertAcknowledgement[];
    clients: ClientInfo[];
    filter: string;
    onFilterChange: (filter: string) => void;
    columnVisibility: ColumnVisibilityState;
    onColumnVisibilityChange: (visibility: ColumnVisibilityState) => void;
    onColumnToggle: (columnId: string, visible: boolean) => void;
    pageSize: number;
    onPageSizeChange: (pageSize: number) => void;
    onBioscopeUpdate: () => void;
    addBioscopeModalIsOpen: boolean;
    onAddBioscopeOpen: () => void;
    onAddBioscopeClose: () => void;
    createBioscope: (clientId: string, bioscopeId: string) => Promise<unknown>;
    /** The `fish-group-tracking` flag. Off, the table and its links are as before fish groups. */
    fishGroupsEnabled: boolean;
}

export default function BioscopeListView(props: BioscopeListViewProps) {
    const {
        hasError,
        retry,
        loading,
        activeCount,
        onlineCount,
        bioscopes,
        alerts,
        acknowledgements,
        clients,
        filter,
        onFilterChange,
        columnVisibility,
        onColumnVisibilityChange,
        onColumnToggle,
        pageSize,
        onPageSizeChange,
        onBioscopeUpdate,
        addBioscopeModalIsOpen,
        onAddBioscopeOpen,
        onAddBioscopeClose,
        createBioscope,
        fishGroupsEnabled,
    } = props;
    const { t } = useTranslation();

    return (
        <PageLayout
            title={t('common.labels.bioscopes')}
            actions={
                <div className={styles.bioscopeNumber}>
                    <Tooltip
                        content={t('internal.bioscopeList.activeTooltip')}
                        placement="bottom"
                        layout="inline">
                        {t('internal.bioscopeList.activeCount')}:<strong>{activeCount}</strong>
                    </Tooltip>
                    <span style={{ marginLeft: 'var(--space-20)' }}>
                        <Tooltip
                            content={t('internal.bioscopeList.onlineTooltip')}
                            placement="bottom"
                            layout="inline">
                            {t('common.labels.online')}:<strong>{onlineCount}</strong>
                        </Tooltip>
                    </span>
                </div>
            }>
            {hasError ? (
                <SomethingHappened {...retry} />
            ) : loading ? (
                <Spinner size="lg" fullScreen />
            ) : (
                <div>
                    <div className={styles.rowContent}>
                        <Card as="section" padding="xl">
                            <div className={styles.toolbar}>
                                <div className={styles.toolbarItem}>
                                    <Button variant="primary" onClick={onAddBioscopeOpen}>
                                        {t('internal.bioscopes.addBioscope')}
                                    </Button>
                                </div>

                                <div className={styles.toolbarEnd}>
                                    <Filter filter={filter} setFilter={onFilterChange} />
                                    <ColumnMenu
                                        columns={COLUMN_METADATA.filter(
                                            (column) =>
                                                fishGroupsEnabled || column.id !== 'fishGroup'
                                        ).map((column) => ({
                                            id: column.id,
                                            label: t(column.header),
                                            visible: columnVisibility[column.id] !== false,
                                        }))}
                                        onToggle={onColumnToggle}
                                    />
                                </div>
                            </div>

                            <BioscopeTable
                                acknowledgements={acknowledgements}
                                alerts={alerts}
                                bioscopes={bioscopes}
                                filter={filter}
                                onBioscopeUpdate={onBioscopeUpdate}
                                columnVisibility={columnVisibility}
                                onColumnVisibilityChange={onColumnVisibilityChange}
                                pageSize={pageSize}
                                onPageSizeChange={onPageSizeChange}
                                fishGroupsEnabled={fishGroupsEnabled}
                            />
                        </Card>
                    </div>
                </div>
            )}
            <AddBioscope
                isOpen={addBioscopeModalIsOpen}
                clients={clients}
                createBioscope={createBioscope}
                onClose={onAddBioscopeClose}
            />
        </PageLayout>
    );
}
